import React, { memo, useCallback, useEffect, useMemo } from 'react';

import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import usePrevious from 'hooks/usePrevious';

import { pagesPropTypes } from '../../../MainContent.constants';

import { getConfig, getModelPath } from './MilitaryServices.config';
import {
  checkAreFieldsRequired,
  customOnFieldChange,
  normalizeForm,
} from './MilitaryServices.utils';

const MilitaryServices = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  setEntityConfig,
  setForm,
  setInForm,
  initialFormValues,
  setValidationErrors,
  disabledPendingChangesHighlight,
  pendingChanges,
}) => {
  const sectionForm = form[entityId]?.declarations?.militaryService ?? {};
  const isEmptyForm = _.isEmpty(sectionForm);
  const modelPath = getModelPath(entityId);

  const initialData = initialFormValues[entityId]?.declarations?.militaryService;

  const {
    basicEntitlement: initialBasicEntitlement,
    bonusEntitlement: initialBonusEntitlement,
    basicEntitlementManualUpdate: initialBasicEntitlementManualUpdate,
    bonusEntitlementManualUpdate: initialBonusEntitlementManualUpdate,
  } = initialData ?? {};

  const {
    isServedInMilitary,
    isCurrentlyServing,
    isCurrentlyRetired,
    isNonActivatedMemberOfReserveNationalGuard,
    isSurvivingSpouse,
    entitlementCodeFromCoe,
    branchOfService,
    militaryStatus,
    basicEntitlementManualUpdate,
    bonusEntitlementManualUpdate,
    basicEntitlement,
    bonusEntitlement,
  } = sectionForm;

  const prevEntityId = usePrevious(entityId);
  const isBorrowerSwitched = entityId !== prevEntityId;
  const prevIsServedInMilitary = usePrevious(isServedInMilitary);
  const prevBasicEntitlementManualUpdate = usePrevious(basicEntitlementManualUpdate);
  const prevBonusEntitlementManualUpdate = usePrevious(bonusEntitlementManualUpdate);

  const isBasicEntitlementWillBeCalculatedAfterSave =
    isServedInMilitary &&
    !basicEntitlementManualUpdate &&
    (initialBasicEntitlementManualUpdate || _.isNil(initialBasicEntitlement));

  const isBonusEntitlementWillBeCalculatedAfterSave =
    isServedInMilitary &&
    !bonusEntitlementManualUpdate &&
    (initialBonusEntitlementManualUpdate || _.isNil(initialBonusEntitlement));

  const areFieldsRequired = checkAreFieldsRequired({
    isServedInMilitary,
    isCurrentlyServing,
    isCurrentlyRetired,
    isNonActivatedMemberOfReserveNationalGuard,
    isSurvivingSpouse,
    entitlementCodeFromCoe,
    branchOfService,
    militaryStatus,
  });

  const onFieldChange = useCallback(
    (e, value) =>
      customOnFieldChange({ e, value, entityId, setForm, setValidationErrors, setInForm }),
    [setInForm, setForm, entityId, setValidationErrors],
  );

  const config = useMemo(
    () =>
      getConfig({
        entityId,
        isServedInMilitary,
        isCurrentlyServing,
        areFieldsRequired,
        onFieldChange,
        isBasicEntitlementWillBeCalculatedAfterSave,
        isBonusEntitlementWillBeCalculatedAfterSave,
        basicEntitlementManualUpdate,
        bonusEntitlementManualUpdate,
      }),
    [
      entityId,
      isServedInMilitary,
      isCurrentlyServing,
      areFieldsRequired,
      isBasicEntitlementWillBeCalculatedAfterSave,
      isBonusEntitlementWillBeCalculatedAfterSave,
      basicEntitlementManualUpdate,
      bonusEntitlementManualUpdate,
      onFieldChange,
    ],
  );

  useEffect(() => {
    if (isBorrowerSwitched) {
      return;
    }

    setForm(form =>
      normalizeForm({
        form,
        entityId,
        initialFormValues,
        prevIsServedInMilitary,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, entityId, setForm, prevIsServedInMilitary, isBorrowerSwitched]);

  useEffect(() => {
    if (!initialData || !_.isNil(initialData.isExemptVAFundingFee)) {
      return;
    }

    setInForm(`${modelPath}isExemptVAFundingFee`, false);
  }, [setInForm, initialData, modelPath]);

  useEffect(() => {
    if (isEmptyForm || !isServedInMilitary || isBorrowerSwitched) {
      return;
    }

    if (
      prevBasicEntitlementManualUpdate !== undefined &&
      basicEntitlementManualUpdate !== prevBasicEntitlementManualUpdate
    ) {
      const basicEntitlementToSet =
        isBasicEntitlementWillBeCalculatedAfterSave ||
        (basicEntitlementManualUpdate && !initialBasicEntitlementManualUpdate)
          ? ''
          : initialBasicEntitlement;

      return setInForm(`${modelPath}basicEntitlement`, basicEntitlementToSet, false);
    }

    if (
      prevBonusEntitlementManualUpdate !== undefined &&
      bonusEntitlementManualUpdate !== prevBonusEntitlementManualUpdate
    ) {
      const bonusEntitlementToSet =
        isBonusEntitlementWillBeCalculatedAfterSave ||
        (bonusEntitlementManualUpdate && !initialBonusEntitlementManualUpdate)
          ? ''
          : initialBonusEntitlement;

      return setInForm(`${modelPath}bonusEntitlement`, bonusEntitlementToSet, false);
    }
  }, [
    isEmptyForm,
    isBasicEntitlementWillBeCalculatedAfterSave,
    basicEntitlementManualUpdate,
    isServedInMilitary,
    setInForm,
    modelPath,
    initialBasicEntitlement,
    initialBonusEntitlement,
    isBonusEntitlementWillBeCalculatedAfterSave,
    bonusEntitlementManualUpdate,
    prevBasicEntitlementManualUpdate,
    prevBonusEntitlementManualUpdate,
    initialBasicEntitlementManualUpdate,
    initialBonusEntitlementManualUpdate,
    isBorrowerSwitched,
  ]);

  useEffect(() => {
    if (isEmptyForm || !isServedInMilitary) {
      return;
    }

    const totalEntitlementAmount =
      isBasicEntitlementWillBeCalculatedAfterSave || isBonusEntitlementWillBeCalculatedAfterSave
        ? ''
        : (Number(basicEntitlement) || 0) + (Number(bonusEntitlement) || 0);

    setInForm(`${modelPath}totalEntitlementAmount`, totalEntitlementAmount, false);
  }, [
    isEmptyForm,
    isServedInMilitary,
    setInForm,
    basicEntitlement,
    bonusEntitlement,
    isBasicEntitlementWillBeCalculatedAfterSave,
    isBonusEntitlementWillBeCalculatedAfterSave,
    modelPath,
  ]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig]);

  return (
    <FormGenerator
      isNestedForm
      changedFields={changedFields}
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.DECLARATIONS}
      handleChange={handleChange}
      isLoading={isSaving || isLoading}
      numberOfColumns={1}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

MilitaryServices.propTypes = pagesPropTypes;

export default memo(MilitaryServices);
